import React, { useState } from 'react';
import { Shield, AlertTriangle, Search, Share2, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { Player } from '../types';
import { InjuryBadge } from './ui/InjuryBadge';
import { INJURY_STATUS_OPTIONS, type InjuryStatus } from '../constants/injuries';
import { InjuryService } from '../services/injuryService';

interface PlayerAvailabilityComponentProps {
  players: Player[];
  teamId: string;
  unavailablePlayers?: Set<string>;
  onUnavailablePlayersChange?: (unavailablePlayers: Set<string>) => void;
  onPlayerUpdate?: (playerId: string, updates: Partial<Player>) => void;
  onInjuryStatusChange?: () => void;
}

export const PlayerAvailabilityComponent: React.FC<PlayerAvailabilityComponentProps> = ({
  players,
  teamId,
  unavailablePlayers: externalUnavailablePlayers,
  onPlayerUpdate,
  onInjuryStatusChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  // Local unavailable players state
  const [localUnavailablePlayers] = useState<Set<string>>(
    externalUnavailablePlayers || new Set()
  );

  // Use external or local unavailable players
  const unavailablePlayers = externalUnavailablePlayers || localUnavailablePlayers;

  // Filter players based on search term
  const filteredPlayers = players.filter(player =>
    `${player.first_name} ${player.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.number.toString().includes(searchTerm)
  );

  // Get MPR players for substitution strategy
  const mprPlayers = players.filter(player => player.playParticipation?.hasMPR);
  const healthyMprPlayers = mprPlayers.filter(player => !unavailablePlayers.has(player.id));
  const healthyMprPlayerCount = healthyMprPlayers.length;
  const availablePlayerCount = players.length - unavailablePlayers.size;
  const unavailableByStatus = players.filter((p) => (p.injury_status || 'H') !== 'H');

  const buildInjuryReportText = () => {
    const lines = [
      `Injury Report`,
      `Generated: ${new Date().toLocaleString()}`,
      `Total Players: ${players.length}`,
      `Available: ${availablePlayerCount}`,
      `Unavailable/Injured: ${unavailableByStatus.length}`,
      ''
    ];

    if (unavailableByStatus.length === 0) {
      lines.push('No players currently marked as unavailable or injured.');
      return lines.join('\n');
    }

    lines.push('Affected Players:');
    unavailableByStatus.forEach((player) => {
      const statusLabel =
        INJURY_STATUS_OPTIONS.find((opt) => opt.value === (player.injury_status || 'H'))?.label || 'Unknown';
      lines.push(`- #${player.number} ${player.first_name} ${player.last_name} (${player.position}) - ${statusLabel}`);
    });

    return lines.join('\n');
  };

  const handleCopyInjuryReport = async () => {
    try {
      await navigator.clipboard.writeText(buildInjuryReportText());
      toast.success('Injury report copied');
    } catch (error) {
      console.error('Failed to copy injury report:', error);
      toast.error('Failed to copy injury report');
    }
  };

  const handleShareInjuryReportByText = async () => {
    const reportText = buildInjuryReportText();

    try {
      if (typeof navigator !== 'undefined' && 'share' in navigator) {
        await (navigator as Navigator & { share: (data: { title?: string; text?: string }) => Promise<void> }).share({
          title: 'Injury Report',
          text: reportText
        });
        toast.success('Injury report ready to send');
        return;
      }

      const smsUrl = `sms:?&body=${encodeURIComponent(reportText)}`;
      window.open(smsUrl, '_self');
      toast.success('Opening text message app');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('Failed to share injury report:', error);
      toast.error('Unable to open text message share');
    }
  };

  // Handle injury status change with auto-save
  const handleInjuryStatusChange = async (playerId: string, injuryStatus: InjuryStatus) => {
    setSaving(prev => ({ ...prev, [playerId]: true }));
    
    try {
      await InjuryService.updatePlayerInjuryStatus(playerId, injuryStatus, teamId);
      
      // Update local players state
      if (onPlayerUpdate) {
        onPlayerUpdate(playerId, { injury_status: injuryStatus });
      }
      
      // Trigger auto-substitution if player is marked as "Out"
      if (injuryStatus === 'O' && onInjuryStatusChange) {
        onInjuryStatusChange();
      }
      
      toast.success('Injury status updated');
    } catch (error) {
      console.error('Error saving injury status:', error);
      toast.error('Failed to save injury status');
    } finally {
      setSaving(prev => ({ ...prev, [playerId]: false }));
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with counts */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <h4 className="text-sm font-medium text-gray-500">
            {availablePlayerCount} Available Players
          </h4>
          <h4 className="text-sm font-medium text-gray-500">
            {healthyMprPlayerCount} MPR Players
          </h4>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Instructions */}
      <div className="p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Player Availability:</strong> All players are assumed to be healthy and available. 
          Set the status of any players who will NOT be available for this game.
        </p>
      </div>

      {/* Injury report summary */}
      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-800">Injury Report</p>
            <p className="text-xs text-gray-600">
              {unavailableByStatus.length === 0
                ? 'No players marked unavailable.'
                : `${unavailableByStatus.length} player${unavailableByStatus.length !== 1 ? 's' : ''} marked unavailable.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleShareInjuryReportByText}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
              aria-label="Share injury report by text message"
              title="Share by text"
            >
              <Share2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={handleCopyInjuryReport}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
              aria-label="Copy injury report"
              title="Copy report"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Players List */}
      <div className="grid gap-3">
        {filteredPlayers.map((player) => {
          const isAvailable = !unavailablePlayers.has(player.id);
          const isMprPlayer = player.playParticipation?.hasMPR;
          const isSaving = saving[player.id];
          
          return (
            <div
              key={player.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                isMprPlayer
                  ? isAvailable 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-red-50 border-red-200'
                  : isAvailable 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex items-center gap-3">
                
                {/* Player Info */}
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-medium text-sm">
                  {player.number}
                </div>
                    <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} />
                {/* MPR Info */}
                {isMprPlayer && (
                    <Shield className="h-4 w-4 text-primary" />
                )}
                <div>
                  <div className="font-medium text-sm flex items-center gap-2">
                    {player.first_name} {player.last_name}
                  </div>
                  <div className="text-xs text-gray-500">{player.position}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">

                                 {/* Injury Status Dropdown */}
                 <select
                   value={player.injury_status || 'H'}
                   onChange={(e) => {
                     const injuryStatus = e.target.value as InjuryStatus;
                     handleInjuryStatusChange(player.id, injuryStatus);
                   }}
                   disabled={isSaving}
                   className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   {INJURY_STATUS_OPTIONS.map(option => (
                     <option key={option.value} value={option.value}>
                       {option.label}
                     </option>
                   ))}
                 </select>

                {/* Saving indicator */}
                {isSaving && (
                  <div className="text-xs text-gray-500">
                    Saving...
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Warnings */}
      {healthyMprPlayerCount === 0 && mprPlayers.length > 0 && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertTriangle size={16} />
            <span className="font-medium">No available MPR players</span>
          </div>
          <p className="text-sm text-yellow-700 mt-1">
            Ensure MPR players are marked as available to include them in the substitution strategy.
          </p>
        </div>
      )}
      
      {availablePlayerCount === 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-800">
            <AlertTriangle size={16} />
            <span className="font-medium">No players available</span>
          </div>
          <p className="text-sm text-red-700 mt-1">
            At least some players must be available to play in the game.
          </p>
        </div>
      )}
    </div>
  );
}; 